/**
 * Payment method information section component
 */

import { CreditCard02 } from '@untitledui/icons';

import { Button } from '@/lib/base/buttons/button';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import type { BillingAddress, PaymentMethod } from '@/lib/billing/types';

import { CardBrandIcon } from './CardBrandIcon';

interface PaymentMethodSectionProps {
  paymentMethod: PaymentMethod | null;
  billingAddress: BillingAddress | null;
  canManageBilling: boolean;
  onUpdatePaymentMethod: () => void;
  portalLoading?: boolean;
}

function formatExpiry(month: number, year: number): string {
  return `${String(month).padStart(2, '0')}/${String(year).slice(-2)}`;
}

function formatAddress(address: BillingAddress): string[] {
  const lines: string[] = [];

  if (address.name) {
    lines.push(address.name);
  }

  if (address.line1) {
    lines.push(address.line1);
  }

  if (address.line2) {
    lines.push(address.line2);
  }

  const cityLine = [address.city, address.state, address.postal_code]
    .filter(Boolean)
    .join(', ');
  if (cityLine) {
    lines.push(cityLine);
  }

  if (address.country && address.country !== 'US') {
    lines.push(address.country);
  }

  return lines;
}

export function PaymentMethodSection({
  paymentMethod,
  billingAddress,
  canManageBilling,
  onUpdatePaymentMethod,
  portalLoading,
}: PaymentMethodSectionProps) {
  return (
    <div className='bg-bg-primary rounded-xl border border-border-secondary shadow-sm overflow-hidden'>
      <div className='px-6 py-5 border-b border-border-secondary'>
        <div className='flex items-center gap-3'>
          <FeaturedIcon
            icon={CreditCard02}
            size='sm'
            theme='modern'
            color='gray'
          />
          <div>
            <h2 className='text-lg font-semibold text-secondary'>
              Payment method
            </h2>
            <p className='text-sm text-tertiary mt-0.5'>
              Your default payment method
            </p>
          </div>
        </div>
      </div>

      <div className='p-6'>
        {paymentMethod ? (
          <div className='space-y-6'>
            {/* Card Info */}
            <div className='flex items-center gap-4'>
              <CardBrandIcon brand={paymentMethod.brand} className='w-12 h-8' />
              <div>
                <p className='text-base font-medium text-primary'>
                  •••• {paymentMethod.last4}
                </p>
                <p className='text-sm text-tertiary'>
                  Expires{' '}
                  {formatExpiry(
                    paymentMethod.exp_month,
                    paymentMethod.exp_year
                  )}
                </p>
              </div>
            </div>

            {/* Billing Address */}
            {billingAddress && (
              <div>
                <p className='text-sm font-medium text-secondary mb-2'>
                  Billing address
                </p>
                <div className='text-sm text-tertiary space-y-0.5'>
                  {formatAddress(billingAddress)
                    .map((line, i) => ({ line, key: `${line}-${i}` }))
                    .map(({ line, key }) => (
                      <p key={key}>{line}</p>
                    ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className='text-center py-4'>
            <p className='text-sm text-tertiary'>No payment method on file</p>
          </div>
        )}

        {/* Admin Actions */}
        {canManageBilling && (
          <div className='mt-6 pt-6 border-t border-border-secondary'>
            <Button
              color='secondary'
              size='sm'
              onClick={onUpdatePaymentMethod}
              isLoading={portalLoading}
              isDisabled={portalLoading}
            >
              {paymentMethod ? 'Update payment method' : 'Add payment method'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
